import React from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { CssBaseline, Box, Slide, Alert, Snackbar } from '@mui/material';
import GlobalTip from '../providers/GlobalTip';
import TopAppBar from '../components/layout/TopAppBar';
import LeftNav from '../components/layout/LeftNav';
import ScrollToTopFab from '../components/layout/ScrollToTopFab';
import Request from "../services/Request";
import CurrentClientContext from '../providers/CurrentClientProvider';
import IndieChatPanelV2 from '../components/orbit/IndieChatPanelV2';
import ClientFeatureFlagService from '../services/ClientFeatureFlagService';
import {
	ORBIT_OPEN_INDIE_PANEL_EVENT,
	getOrbitPageAssistantState,
	subscribeOrbitPageAssistantState,
} from '../services/orbit/orbitPageAssistantBridge';

const drawerWidth = 80;

function MessageTransitionDown(props) {
	return <Slide {...props} direction="down" />;
}

function Layout() {
	let globalTipContext = GlobalTip.useMessage();
	const currentClient = CurrentClientContext.useClient();
	const location = useLocation();
	const navigate = useNavigate();
	const [navOpen, setNavOpen] = React.useState(false);
	const [showAlert, setShowAlert] = React.useState(false);
	const [banners, setBanners] = React.useState([]);
	const [indiePanelMode, setIndiePanelMode] = React.useState('closed');
	const [pageAssistant, setPageAssistant] = React.useState(() => getOrbitPageAssistantState());
	const isFullPageChatRoute = /\/clients\/[^/]+\/indie-ai$/.test(location.pathname);
	const hasAssignedClient = Boolean(currentClient?.client?.Id ?? currentClient?.client?.id);
	const isAiChatBotEnabled = hasAssignedClient && ClientFeatureFlagService.isClientFeatureFlagEnabled(currentClient?.client);
	const hideTopBarIndieButton = isFullPageChatRoute || !isAiChatBotEnabled;
	const activeIndiePanelMode = isFullPageChatRoute ? 'closed' : indiePanelMode;
// Add this calculation at the top of your component
	const bannerHeight = banners.length * 60;
	const indiePanelWidth = 460;
	const indiePanelTop = showAlert ? (banners.length * 60 + 62) : 62;

	const cssInJs = {
		root: {
			minHeight: '100vh',
			display: 'flex',
			flexDirection: 'column',
			// Target the specific MUI components with nested selectors
			'& header.MuiPaper-root': {
				marginTop: showAlert ? bannerHeight + 'px' : 0,
			},
			'& .MuiPaper-root.MuiPaper-elevation.MuiDrawer-paper.MuiDrawer-paperAnchorLeft.MuiDrawer-paperAnchorDockedLeft': {
				marginTop: showAlert ? bannerHeight + 'px' : 0,
			}
		},
		bannerContainer: {
			position: 'fixed',
			width: '100%',
			top: 0,
			zIndex: 1301, // Higher z-index to ensure visibility
			display: 'flex',
			flexDirection: 'column'
		},
		appContainer: {
			display: 'flex',
			flexDirection: 'column',
			flexGrow: 1,
		},
		appBar: {
			position: 'fixed',
			top: showAlert ? (banners.length * 60) + 'px' : 0
		},
		leftNav: {
			'& .MuiPaper-root': {
				marginTop: showAlert ? (banners.length * 60 + 50) + 'px' : '50px'
			}
		},
		contentSection: {
			flexGrow: 1,
			// Full-page chat routes get zero padding so IndieChatFull fills the viewport
			px: isFullPageChatRoute ? 0 : 2,
			py: isFullPageChatRoute ? 0 : undefined,
			mt: showAlert ? (banners.length * 60 + 50) + 'px' : '50px',
			ml: isFullPageChatRoute ? 0 : 2,
			mr: activeIndiePanelMode === 'docked' || activeIndiePanelMode === 'page' ? `calc(${indiePanelWidth}px + 32px)` : (isFullPageChatRoute ? 0 : 2),
			overflow: isFullPageChatRoute ? 'hidden' : undefined,
			height: isFullPageChatRoute ? (showAlert ? `calc(100vh - ${banners.length * 60 + 50}px)` : 'calc(100vh - 50px)') : undefined,
			transition: 'margin-right 0.25s ease'
		},
		alert: {
			width: '100%',
			minHeight: '60px',
			'& a': {
				textDecoration: 'none',
				borderBottom: '1px solid',
				color: 'inherit'
			},
			border: '1px dotted',
		}
	};

	const handleDrawerToggle = () => {
		setNavOpen(!navOpen);
	};

	const openIndieFlyout = () => {
		if (!isAiChatBotEnabled) {
			return;
		}
		setIndiePanelMode('flyout');
	};

	const closeIndiePanel = () => {
		setIndiePanelMode('closed');
	};

	const toggleIndieDock = () => {
		setIndiePanelMode((current) => current === 'docked' ? 'flyout' : 'docked');
	};

	const openIndieFullView = (conversationId) => {
		if (!isAiChatBotEnabled) {
			return;
		}
		const clientCodeFromContext = currentClient?.client?.ClientCode || currentClient?.client?.client_code;
		const clientCodeFromPath = location.pathname.match(/\/clients\/([^/]+)/)?.[1];
		const clientCode = clientCodeFromContext || clientCodeFromPath;
		if (!clientCode) return;

		const params = new URLSearchParams();
		if (conversationId) {
			params.set('conversation_id', conversationId);
		}
		setIndiePanelMode('closed');
		navigate(`/clients/${clientCode}/indie-ai${params.toString() ? `?${params.toString()}` : ''}`);
	};

	React.useEffect(() => {
		Request.get('/api/banner-status', {credentials: 'include'}, true).then(response => {
			console.log('Full response:', response);

			// The data is directly in response.data, not in response.data.data
			if (response.data && response.data.shouldShowAlert) {
				console.log('Setting banners:', response.data.banners);
				setBanners(response.data.banners || []);
				setShowAlert(true);
			} else {
				console.warn('No banners or shouldShowAlert flag found');
			}
		})
			.catch(error => console.error('Error fetching banners:', error));
	}, []);

	React.useEffect(() => {
		if (isFullPageChatRoute) {
			setIndiePanelMode('closed');
		}
	}, [isFullPageChatRoute]);

	React.useEffect(() => {
		if (!isAiChatBotEnabled && indiePanelMode !== 'closed') {
			setIndiePanelMode('closed');
		}
	}, [isAiChatBotEnabled, indiePanelMode]);

	React.useEffect(() => {
		const unsubscribe = subscribeOrbitPageAssistantState((nextState) => {
			setPageAssistant(nextState);
		});

		const handleOpenPanelRequest = () => {
			if (!isFullPageChatRoute && isAiChatBotEnabled) {
				setIndiePanelMode('flyout');
			}
		};

		window.addEventListener(ORBIT_OPEN_INDIE_PANEL_EVENT, handleOpenPanelRequest);

		return () => {
			unsubscribe();
			window.removeEventListener(ORBIT_OPEN_INDIE_PANEL_EVENT, handleOpenPanelRequest);
		};
	}, [isAiChatBotEnabled, isFullPageChatRoute]);


	return (
	<Box sx={cssInJs.root}>
		{showAlert && banners.length > 0 && (
			<Box sx={cssInJs.bannerContainer}>
				{banners.map((banner) => (
					<Alert
						key={banner.id}
						severity="warning"
						sx={cssInJs.alert}
					>
						<div dangerouslySetInnerHTML={{ __html: banner.content }} />
					</Alert>
				))}
			</Box>
		)}

		<CssBaseline />

			<Box sx={cssInJs.appContainer}>
				<TopAppBar id={'app-bar-global'}
						   width={drawerWidth}
						   onOpenIndieChat={openIndieFlyout}
						   isIndieChatOpen={indiePanelMode !== 'closed'}
						   showIndieChatButton={!hideTopBarIndieButton}
						   sx={cssInJs.appBar}
				/>
				<Box sx={{ display: 'flex', flexGrow: 1 }}>
					<LeftNav id={'left-nav-global'}
							 width={drawerWidth}
							 navOpen={navOpen}
							 onNavClose={handleDrawerToggle}
							 sx={cssInJs.leftNav}
					/>
					<Box component="main" sx={cssInJs.contentSection}>
						<Outlet />
					</Box>
				</Box>
			</Box>

			{isAiChatBotEnabled && activeIndiePanelMode !== 'closed' && (
				<Box
					sx={{
						position: 'fixed',
						top: indiePanelTop,
						right: 16,
						bottom: 16,
						width: { xs: 'calc(100vw - 32px)', md: indiePanelWidth },
						zIndex: 1302,
						pointerEvents: 'auto',
						transition: 'all 0.25s ease',
					}}
				>
					<IndieChatPanelV2
						mode={activeIndiePanelMode}
						clientId={currentClient?.client?.Id ?? currentClient?.client?.id ?? 0}
						client={currentClient?.client}
						pageAssistant={pageAssistant}
						onClose={activeIndiePanelMode === 'page' ? undefined : closeIndiePanel}
						onToggleDock={activeIndiePanelMode === 'page' ? undefined : toggleIndieDock}
						onOpenFullView={openIndieFullView}
					/>
				</Box>
			)}

			<ScrollToTopFab hidden={activeIndiePanelMode !== 'closed'} />

			<Snackbar open={globalTipContext.messageOpen} TransitionComponent={MessageTransitionDown} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
				<Alert variant="filled" severity="warning" sx={{ width: '100%' }}>
					{globalTipContext.messageContent}
				</Alert>
			</Snackbar>
			<Snackbar open={globalTipContext.successOpen} TransitionComponent={MessageTransitionDown} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
				<Alert variant="filled" severity="success" sx={{ width: '100%' }}>
					{globalTipContext.successContent}
				</Alert>
			</Snackbar>
		</Box>
	);
}

export default Layout;